import { DateTime, Interval } from "luxon";
import { MDSNotePreviewContainerFooterProps } from "./note-preview-container-footer.types";
import {
  StyledFooter,
  StyledFooterMetadata,
  StyledFooterTitle,
} from "./note-preview-container-footer.style";

export const MDSNotePreviewContainerFooter = ({
  title,
  createdAt,
  modifiedAt,
  secondaryLabel,
  authorNames,
  updateOnly = false,
}: MDSNotePreviewContainerFooterProps) => {
  const metadata = generateNoteMetadata({
    createdAt,
    modifiedAt,
    authorNames,
    updateOnly,
    secondaryLabel,
  });

  return (
    <StyledFooter>
      <StyledFooterTitle>{title}</StyledFooterTitle>
      <StyledFooterMetadata>{metadata}</StyledFooterMetadata>
    </StyledFooter>
  );
};

/**
 * Renders an array of strings with proper "and" formatting.
 * Examples: ["John"] -> "John", ["John", "Jane"] -> "John and Jane", ["A", "B", "C"] -> "A, B, and C"
 */
const renderArrayWithAnds = (array: string[]): string => {
  if (!array.length) {
    return "";
  }

  if (array.length === 1) {
    return array[0];
  }

  if (array.length === 2) {
    return `${array[0]} and ${array[1]}`;
  }

  const preOxfordComma = array.slice(0, array.length - 1).join(", ");
  return `${preOxfordComma}, and ${array[array.length - 1]}`;
};

/**
 * Converts a date to a recent date string like "Today", "Yesterday", "Monday", "Jan 5", "1/5/23"
 */
const generateRecentDateString = (dateInput: string | Date): string => {
  const date =
    typeof dateInput === "string" ? DateTime.fromISO(dateInput) : DateTime.fromJSDate(dateInput);
  if (!date.isValid) {
    return "";
  }

  const now = DateTime.local().endOf("day");

  // Handle future dates safely - if date is after now, treat as "Today"
  if (date > now) {
    return "Today";
  }

  const interval = Interval.fromDateTimes(date, now);
  if (!interval.isValid) {
    return "Today"; // Fallback for any interval issues
  }

  const dateDiff = interval.length("days");

  // Additional safety check for NaN
  if (isNaN(dateDiff)) {
    return "Today";
  }

  let dateString = "";
  if (dateDiff < 1) {
    dateString = "Today";
  } else if (dateDiff < 2) {
    dateString = "Yesterday";
  } else if (dateDiff < 7) {
    dateString = date.toFormat("cccc"); // Full day name
  } else if (date.hasSame(now, "year")) {
    dateString = date.toFormat("MMM d");
  } else {
    dateString = date.toFormat("M/d/yy");
  }

  return dateString.charAt(0).toUpperCase() + dateString.slice(1);
};

/**
 * Generates note metadata string combining creation date, modification date (optional), and author names.
 * Format: "Today | Yesterday — John Doe" or "Today — John and Jane"
 */
const generateNoteMetadata = ({
  createdAt,
  modifiedAt,
  authorNames,
  secondaryLabel,
  updateOnly = false,
}: {
  createdAt: string | Date;
  modifiedAt?: string | Date;
  authorNames: string[];
  secondaryLabel?: string | null;
  updateOnly?: boolean;
}): string => {
  const createdDateString = generateRecentDateString(createdAt);
  const authorsString = secondaryLabel ?? renderArrayWithAnds(authorNames);

  if (updateOnly || !modifiedAt) {
    return `${createdDateString} — ${authorsString}`;
  }

  const createdDate =
    typeof createdAt === "string" ? DateTime.fromISO(createdAt) : DateTime.fromJSDate(createdAt);
  const modifiedDate =
    typeof modifiedAt === "string" ? DateTime.fromISO(modifiedAt) : DateTime.fromJSDate(modifiedAt);

  if (createdDate.hasSame(modifiedDate, "day")) {
    return `${createdDateString} — ${authorsString}`;
  }

  const modifiedDateString = generateRecentDateString(modifiedAt);
  return `${createdDateString} | ${modifiedDateString} — ${authorsString}`;
};
